import { useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
/**
 * The single owner of a tool's Cesium CustomDataSource: created and attached
 * when the viewer appears, detached when the viewer changes or the caller
 * unmounts. Every tool hook goes through this so there is one teardown path
 * rather than a hand-rolled effect per hook.
 *
 * `configure` runs on the data source after construction but before it is
 * attached — use it for create-time flags (e.g. the terrain-sync tag) that
 * must be in place before the first render pass sees the source.
 */
export const useCustomDataSource = (viewer, name, configure) => {
    const ref = useRef(null);
    // Held in a ref so an inline callback at the call site can't churn the
    // effect and tear the data source down on every render.
    const configureRef = useRef(configure);
    configureRef.current = configure;
    useEffect(() => {
        var _a;
        if (!viewer || viewer.isDestroyed())
            return;
        const ds = new Cesium.CustomDataSource(name);
        (_a = configureRef.current) === null || _a === void 0 ? void 0 : _a.call(configureRef, ds);
        viewer.dataSources.add(ds);
        ref.current = ds;
        return () => {
            if (!viewer.isDestroyed()) {
                viewer.dataSources.remove(ds);
            }
            ref.current = null;
        };
    }, [viewer, name]);
    return ref;
};
